import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(api.getStoredUser());
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = api.getToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const userData = await api.getMe();
      setUser(userData);
      api.setStoredUser(userData);
      // Auto-resubscribe to push on every visit for logged-in users
      import('../utils/pushNotifications').then(({ isPushSupported, getPermissionState, subscribeToPush }) => {
        if (isPushSupported() && getPermissionState() === 'granted') {
          subscribeToPush().catch(() => {});
        }
      }).catch(() => {});
    } catch {
      setUser(null);
      api.clearToken();
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    // Handle Facebook OAuth callback token
    if (window.location.hash?.includes('fb_token=')) {
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);
      const token = params.get('fb_token');
      if (token) {
        api.setToken(token);
        // Clear the hash
        window.history.replaceState(null, '', window.location.pathname);
        refreshUser();
        return;
      }
    }
    refreshUser();
  }, [refreshUser]);

  useEffect(() => {
    const handleLogout = () => setUser(null);
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  const login = async (email, password, recaptchaToken) => {
    const data = await api.login(email, password, recaptchaToken);
    // If email not verified, don't set user — return the response for the UI to handle
    if (data.error === 'email_not_verified') {
      return data;
    }
    setUser(data.user);
    // Auto-request push notification permission on login
    try {
      const { isPushSupported, subscribeToPush } = await import('../utils/pushNotifications');
      if (isPushSupported()) {
        subscribeToPush().catch(() => {});
      }
    } catch { /* silent */ }
    return data;
  };

  const signup = async (name, email, password, phone, recaptchaToken, referralCode) => {
    const data = await api.signup(name, email, password, phone, recaptchaToken, referralCode);
    // Signup no longer returns a token — user must verify email first
    return data;
  };

  const logout = () => {
    api.logout();
    localStorage.removeItem('gopiko_post_ad_draft');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, signup, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
